import React, { useCallback } from 'react'
import { VrModuleTitle, VrModuleInfo } from '../layout/vr-module-title'
import { css } from '@emotion/css'
import { ErrorBoundary } from '../../../../legacy-design-system/error-boundary/error-boundary'
import { theme } from '@contrast-security-inc/design-system-foundations'
import {
  LicenseThermometerLoadingView,
  LicenseThermometerProps
} from '../../../thermometer/license-thermometer'
import { LicenseThermometerSkeletor } from '../../../thermometer/license-thermometer-skeletor'
import { FormattedMessage, useIntl } from 'react-intl'
import { LoadingMaskRectangle } from '../../../../legacy-design-system/loading-masks'
import { ValueReportingDashboardProps } from '../../vr-main'
import {
  useVrApiExportDialog,
  VrApiExportDialog
} from '../../export/vr-api-export-dialog'
import { getLicensesCurlCommand } from '../../export/vr-curl-commands'
import { VrStandaloneModulePanel } from '../layout/vr-module-containers'
import { useVrLicenses } from './use-vr-licenses'
import { VrLicenseData } from './api/queries/vr-licenses'
import { Link, Text } from '@contrast-security-inc/design-system-typography'
import { formatStyles } from '../../../react-intl-helpers'

export const vrLicenseThermometerHeaderTestId =
  'vr-license-thermometer-header-test-id'

const thermometerHeaderRowCss = css`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 7px;
`

const thermometerAssessRowContainerCss = css`
  margin: 0 0 15px 0;
`

const thermometerProtectRowContainerCss = css`
  margin: 30px 0 0 0;
`

type LicenseThermometerType = 'assess' | 'protect'

const thermometerRowTypeConfiguration: {
  [key in LicenseThermometerType]: {
    totalLicensesId: string
    unlicensedTextId: string
    unlicensedLinkPath: string
    licensedLinkPath: string
  }
} = {
  assess: {
    totalLicensesId: 'detail_valueReporting_totalAssessLicenses',
    unlicensedTextId: 'variable_valueReporting_youHaveUnlicensedApplications',
    unlicensedLinkPath: 'applications?quickFilter=UNLICENSED',
    licensedLinkPath: 'applications?quickFilter=LICENSED'
  },
  protect: {
    totalLicensesId: 'detail_valueReporting_totalProtectLicenses',
    unlicensedTextId: 'variable_valueReporting_youHaveUnlicensedServers',
    unlicensedLinkPath: 'servers?quickFilter=UNPROTECTED',
    licensedLinkPath: 'servers?quickFilter=PROTECTED'
  }
}

const Layout = ({
  protectEnabled,
  renderTitle,
  renderSubtitle,
  renderAssessLicensesRow,
  renderProtectLicensesRow
}: {
  protectEnabled?: boolean
  renderTitle: () => React.ReactNode
  renderSubtitle: () => React.ReactNode
  renderAssessLicensesRow: () => React.ReactNode
  renderProtectLicensesRow: () => React.ReactNode
}) => {
  return (
    <>
      <div>
        {renderTitle()}
        {renderSubtitle()}
      </div>
      <div>
        <div className={thermometerAssessRowContainerCss}>
          {renderAssessLicensesRow()}
        </div>
        {protectEnabled && (
          <div className={thermometerProtectRowContainerCss}>
            {renderProtectLicensesRow()}
          </div>
        )}
      </div>
    </>
  )
}

interface RowProps {
  total: number
  unlicensed: number
}

export type LicenseThermometerRowProps = LicenseThermometerProps & RowProps

function LicenseThermometerRow({
  organizationUuid,
  total,
  unlicensed,
  type,
  used
}: {
  organizationUuid: string
  type: LicenseThermometerType
} & LicenseThermometerRowProps) {
  const intl = useIntl()
  const rowTypeConfiguration = thermometerRowTypeConfiguration[type]

  const excess = used - total
  const totalUsed = excess > 0 ? total : used

  return total > 0 ? (
    <>
      <div
        className={thermometerHeaderRowCss}
        data-testid={`${type}-${vrLicenseThermometerHeaderTestId}`}
      >
        <Text font="body">
          {intl.formatMessage({
            id:
              type === 'protect'
                ? 'title_protectLicenses'
                : 'title_assessLicenses'
          })}
        </Text>
        <Text font="metadata">
          <FormattedMessage
            id={rowTypeConfiguration.unlicensedTextId}
            values={{
              a: (...msg) => (
                <Link
                  href={`#/${organizationUuid}/${rowTypeConfiguration.unlicensedLinkPath}`}
                >
                  {msg}
                </Link>
              ),
              count: unlicensed
            }}
          />
        </Text>
      </div>

      <LicenseThermometerSkeletor
        LinkProps={{
          href: `#/${organizationUuid}/${rowTypeConfiguration.licensedLinkPath}`
        }}
        excess={
          excess > 0
            ? {
                value: excess,
                TooltipProps: {
                  body: intl.formatMessage(
                    {
                      id:
                        type === 'assess'
                          ? 'detail_valueReporting_licensesBar_assessExcess'
                          : 'detail_valueReporting_licensesBar_protectExcess'
                    },
                    {
                      excess,
                      ...formatStyles.bold
                    }
                  )
                }
              }
            : undefined
        }
        total={total}
        used={{
          value: totalUsed,
          TooltipProps: {
            body: intl.formatMessage(
              {
                id:
                  type === 'assess'
                    ? 'detail_valueReporting_licensesBar_assess'
                    : 'detail_valueReporting_licensesBar_protect'
              },
              {
                total,
                used: totalUsed,
                ...formatStyles.bold
              }
            )
          }
        }}
        testId={`vr-license-thermometer-${type}`}
      />
    </>
  ) : null
}

function LicenseThermometerRowLoadingView() {
  return (
    <>
      <div className={thermometerHeaderRowCss}>
        <LoadingMaskRectangle width={200} height={22} />
        <div
          className={css`
            color: ${theme.color.neutral60};
          `}
        >
          <LoadingMaskRectangle width={200} height={22} />
        </div>
      </div>
      <LicenseThermometerLoadingView />
    </>
  )
}

export const licenseModuleTestId = 'vr-licenses-module-test-id'

interface VrLicensesLoaderProps {
  organizationUuid: string
  protectEnabled?: boolean
}

function VrLicensesLoader({
  organizationUuid,
  protectEnabled
}: VrLicensesLoaderProps) {
  const { vrLicensesData, isLoading, error } = useVrLicenses(organizationUuid)

  if (error) {
    console.error(error.message)
    throw new Error('Failed getting VR licenses data')
  }

  // eslint-disable-next-line unicorn/prefer-ternary
  if (isLoading || !vrLicensesData) {
    return <LoadingView />
  } else {
    return (
      <DataView
        organizationUuid={organizationUuid}
        data={vrLicensesData}
        protectEnabled={protectEnabled}
      />
    )
  }
}

function LicenseThermometerHeaderLoadingView() {
  return <LoadingMaskRectangle width={200} height={28} />
}

function LoadingView() {
  return (
    <Layout
      protectEnabled={true}
      renderTitle={() => <LicenseThermometerHeaderLoadingView />}
      renderSubtitle={() => <LicenseThermometerHeaderLoadingView />}
      renderAssessLicensesRow={() => <LicenseThermometerRowLoadingView />}
      renderProtectLicensesRow={() => <LicenseThermometerRowLoadingView />}
    />
  )
}

function DataView({
  organizationUuid,
  data: { assess, protect },
  protectEnabled
}: {
  organizationUuid: string
  data: VrLicenseData
  protectEnabled?: boolean
}) {
  const renderAssess = useCallback(
    () => (
      <LicenseThermometerRow
        type="assess"
        organizationUuid={organizationUuid}
        {...assess}
      />
    ),
    [organizationUuid, assess]
  )

  const renderProtect = useCallback(
    () => (
      <LicenseThermometerRow
        type="protect"
        organizationUuid={organizationUuid}
        {...protect}
      />
    ),
    [organizationUuid, protect]
  )

  return (
    <Layout
      protectEnabled={protectEnabled}
      renderTitle={() => (
        <VrModuleTitle id="title_valueReporting_licensedUsed" />
      )}
      renderSubtitle={() => (
        <VrModuleInfo id="detail_dashboard_valueReporting_filterInfo" />
      )}
      renderAssessLicensesRow={renderAssess}
      renderProtectLicensesRow={renderProtect}
    />
  )
}

export type VrModuleLicensesProps = ValueReportingDashboardProps & {
  organizationUuid: string
  protectEnabled?: boolean
}

export function VrModuleLicenses({
  organizationUuid,
  hostUrl,
  authHeader,
  apiKey,
  protectEnabled
}: VrModuleLicensesProps) {
  const { openDialog, dialogProps } = useVrApiExportDialog()
  return (
    <>
      <VrStandaloneModulePanel
        hasBorder={true}
        order={4}
        minHeight={300}
        onExportClick={openDialog}
        testId={licenseModuleTestId}
      >
        <ErrorBoundary>
          <VrLicensesLoader
            organizationUuid={organizationUuid}
            protectEnabled={protectEnabled}
          />
        </ErrorBoundary>
      </VrStandaloneModulePanel>
      <VrApiExportDialog
        {...dialogProps}
        titleId="title_valueReporting_adoptionApi"
        subtitleId="detail_valueReporting_selectedFiltersDoNotApplyToLicenseData"
        sections={[
          {
            curlCommand: getLicensesCurlCommand({
              protocolAndHost: hostUrl,
              organizationUuid,
              authHeader,
              apiKey
            })
          }
        ]}
        e2eTestId="value-reporting-licenses-dialog"
      />
    </>
  )
}
